import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { ActivityPreferencesService } from '../services/activity-preferences.service';
import {
  FollowGeneralForSportAction,
  LoadMyActivityPreferencesAction,
  SaveGeneralActivityPreferencesAction,
  SaveSportActivityPreferencesAction,
  ToggleAvailabilitySlotAction,
} from './activity-preferences.actions';
import {
  ActivitySlot,
  MyActivityPreferences,
  SportActivityPreferencesView,
  toggleSlot,
} from '../domains/activity-preferences';

export interface ActivityPreferencesStateModel {
  loading: boolean;
  /** What the API answered, already resolved. Never edited in place. */
  preferences?: MyActivityPreferences;
  /**
   * The grid currently being edited, before anybody presses save.
   *
   * Kept apart from `preferences` on purpose: somebody who ticks three cells
   * and leaves without saving must find the screen as it was, not as they left
   * it. The key is the sport, or 'general'.
   */
  draftAvailability: Record<string, ActivitySlot[]>;
}

const GENERAL = 'general';

const defaults: ActivityPreferencesStateModel = {
  loading: false,
  preferences: undefined,
  draftAvailability: {},
};

@State<ActivityPreferencesStateModel>({
  name: 'activityPreferences',
  defaults,
})
@Injectable()
export class ActivityPreferencesState {
  constructor(private readonly _service: ActivityPreferencesService) {}

  @Action(LoadMyActivityPreferencesAction)
  async load(ctx: StateContext<ActivityPreferencesStateModel>): Promise<void> {
    ctx.patchState({ loading: true });
    try {
      const preferences = await firstValueFrom(this._service.getMine());
      // The draft is dropped on every load: what the server holds is the truth
      // the screens draw from.
      ctx.patchState({ preferences, draftAvailability: {}, loading: false });
    } catch {
      // A failed read leaves the screens on what they already had rather than
      // on an empty page, and the loading flag has to come down either way.
      ctx.patchState({ loading: false });
    }
  }

  @Action(ToggleAvailabilitySlotAction)
  toggle(
    ctx: StateContext<ActivityPreferencesStateModel>,
    { payload }: ToggleAvailabilitySlotAction,
  ): void {
    const key = payload.sportId ?? GENERAL;
    const state = ctx.getState();
    const current = state.draftAvailability[key] ?? this.savedAvailabilityFor(state, payload.sportId);

    ctx.patchState({
      draftAvailability: {
        ...state.draftAvailability,
        [key]: toggleSlot(current, payload.day, payload.moment),
      },
    });
  }

  @Action(SaveGeneralActivityPreferencesAction)
  async saveGeneral(
    ctx: StateContext<ActivityPreferencesStateModel>,
    { payload }: SaveGeneralActivityPreferencesAction,
  ): Promise<void> {
    await firstValueFrom(this._service.updateGeneral(payload));
    await this.load(ctx);
  }

  @Action(SaveSportActivityPreferencesAction)
  async saveSport(
    ctx: StateContext<ActivityPreferencesStateModel>,
    { payload }: SaveSportActivityPreferencesAction,
  ): Promise<void> {
    const { sportId, ...preferences } = payload;
    await firstValueFrom(this._service.updateSport(sportId, preferences));
    await this.load(ctx);
  }

  @Action(FollowGeneralForSportAction)
  async followGeneral(
    ctx: StateContext<ActivityPreferencesStateModel>,
    { payload }: FollowGeneralForSportAction,
  ): Promise<void> {
    await firstValueFrom(this._service.clearSport(payload.sportId));
    await this.load(ctx);
  }

  /** What the server holds for that grid, general or per sport. */
  private savedAvailabilityFor(
    state: ActivityPreferencesStateModel,
    sportId?: string,
  ): ActivitySlot[] {
    if (!sportId) {
      return state.preferences?.general.availability ?? [];
    }

    const sport: SportActivityPreferencesView | undefined = state.preferences?.sports.find(
      (line) => line.sportId === sportId,
    );

    return sport?.availability ?? [];
  }
}
